import { MouseEvent } from 'react';

import { Point, SeriesClickEventObject } from 'highcharts';
import { find, get, each, map, groupBy, keyBy, flatten, round, clone, remove, startsWith } from 'lodash';
import { DateTime, DateTimeFormatOptions } from 'luxon';
import numeral from 'numeral';
import { TFunction } from 'react-i18next';

import { Source } from 'admin/sources/Sources';
import { CommentType, CommentProperty, FieldName, Sentiment, SystemLabel } from 'common/comments2/Comments2.types';
import { Group } from 'common/util/DateRange';
import { safeNumeralFormat } from 'core/chart/Util';
import { Interval } from 'core/util/Aggregate';
import { BatchAggregateRequest, MetricType } from 'core/util/Aggregate';
import { Filter, SortOrder } from 'core/util/Search';
import { Color, SentimentColor } from 'core/util/Variables';
import { ReviewMetric, OverviewMetrics, MetricId, MetricMetadata } from 'reviews2/feed/metrics/Metrics.types';

const LIMIT = 250;

export enum ChartArea {
    Legend,
    Column,
}

interface GetChartArgs {
    t: TFunction;
    sources?: Source[];
    dateRangeSet?: any;
    handleColumnClick?: (event: any, value: any, sourceId?: string, date?: string) => void;
    handleLegendClick?: (event: any, value: any, sourceId?: string, date?: string) => void;
    isYetiEnabled: boolean;
}

interface HighchartPoint extends Point {
    date: string;
    dateRangeGroup: number;
    fiscalDateRange?: string;
}

export const roundRating = (rating: number) => round(rating, 1);
export const formatMetric = (metric: number) => numeral(metric).format('0,0');
export const DEFAULT_SELECTED_CHART = ReviewMetric.SentimentTrend;

export const getDateFormatForGroup = (group: number): DateTimeFormatOptions => {
    switch (group) {
        case Group.Day:
        case Group.Week:
        case Group.DayOfWeek:
        case Group.TimeOfDay:
        case Group.FiscalWeek:
        default: {
            return { weekday: 'short', month: 'short', day: '2-digit', year: 'numeric' };
        }
        case Group.Month:
        case Group.Quarter:
        case Group.FiscalMonth:
        case Group.FiscalQuarter:
        case Group.FiscalYear: {
            return { month: 'short', year: 'numeric' };
        }
        case Group.Year: {
            return { year: 'numeric' };
        }
    }
};

export const getAggregateReqs = (
    metric: ReviewMetric,
    filters: Filter[],
    dateRangeSet?: any,
): BatchAggregateRequest => {
    switch (metric) {
        case ReviewMetric.Overview: {
            return getReviewsOverviewReqs(filters);
        }
        case ReviewMetric.AverageRating: {
            return getAverageRatingReqs(filters);
        }
        case ReviewMetric.SentimentTrend: {
            return getSentimentTrendReqs(filters, dateRangeSet);
        }
        case ReviewMetric.SentimentBySource: {
            return getSentimentBySourceReqs(filters);
        }
        case ReviewMetric.ResponseStatus: {
            return getRespondedReqs(filters, dateRangeSet);
        }
        case ReviewMetric.TopCategories: {
            return getTopCategoriesReqs(filters);
        }
        case ReviewMetric.SentimentBreakdown: {
            return getSentimentBreakdownReqs(filters);
        }
        case ReviewMetric.LocationsCount: {
            return getLocationsCountReqs(filters);
        }
        default:
            throw new Error(`Metrics.util: unhandled metric: ${metric}`);
    }
};

export const getChartOptions = (metric: ReviewMetric, data: any[], args: GetChartArgs) => {
    const chart = getChartData(metric, data, args);

    return {
        chart: {
            type: 'column',
            height: 300,
            spacingTop: 24,
            style: {
                color: Color.Grey10,
            },
            alignTicks: false,
            numberFormatter: (...args: any[]) => formatMetric(args[0]),
        },
        subtitle: {
            useHTML: true,
            align: 'right',
            verticalAlign: 'bottom',
        },
        legend: {
            enabled: true,
            align: 'center',
            verticalAlign: 'bottom',
        },
        plotOptions: {
            column: {
                stacking: 'normal',
            },
            series: {
                cursor: 'pointer',
            },
        },
        tooltip: {
            shared: false,
            formatter: function formatter(): string {
                const { point, x, y } = this as any;

                return `<span>${x}</span><br /><span>${point.series.name}</span>:&nbsp;<b>${safeNumeralFormat(
                    y,
                    '0,0[.][00]',
                )}</b><br />`;
            },
        },
        xAxis: {
            type: 'linear',
            ordinal: true,
            labels: {
                enabled: true,
                style: {
                    color: Color.Black,
                },
            },
            categories: chart.categories,
        },
        yAxis: chart.yAxis,
        series: chart.series,
        title: { text: null },
    };
};

export const convertToCategoryMetrics = (reviewMetric: ReviewMetric, data: any[], args?: any) => {
    const aggregates = data[0];
    const { categories } = args;
    const categoriesById = keyBy(categories, 'id');

    return aggregates?.map((metric: any) => {
        const category = get(categoriesById, metric[FieldName.CategoryId]);

        return {
            id: category?.id,
            label: category?.displayName,
            value: formatMetric(metric.metric),
        };
    });
};

export const convertToAverageRatingMetrics = (data: any[], args?: any) => {
    const [aggregates] = data;

    return aggregates?.[0]?.metric;
};

const getChartData = (reviewMetric: ReviewMetric, data: any[], args: GetChartArgs) => {
    switch (reviewMetric) {
        case ReviewMetric.SentimentTrend: {
            return getSentimentTrendData(data, args);
        }
        case ReviewMetric.SentimentBySource: {
            return getSentimentBySourceData(data, args);
        }
        case ReviewMetric.ResponseStatus: {
            return getRespondedData(data, args);
        }
        default:
            throw new Error(`Metrics.util: unhandled ReviewMetric: ${ReviewMetric}`);
    }
};

const getDateFilterValue = (dateStr: string, interval: Interval) => {
    const date = DateTime.fromISO(dateStr);

    if (!date.isValid) {
        return undefined;
    }
    let startFormatted;
    let endFormatted;

    if (interval === Interval.Day) {
        startFormatted = `${date.toFormat('MM/dd/yyyy')} 00:00:00`;
        endFormatted = `${date.toFormat('MM/dd/yyyy')} 23:59:59`;
    } else if (interval === Interval.Month) {
        startFormatted = `${date.startOf('month').toFormat('MM/dd/yyyy')} 00:00:00`;
        endFormatted = `${date.endOf('month').toFormat('MM/dd/yyyy')} 23:59:59`;
    }

    return `${startFormatted}$$${endFormatted}`;
};

const getSentimentTrendData = (data: any[], args: GetChartArgs) => {
    const { t, dateRangeSet, handleColumnClick, handleLegendClick } = args;
    const [sentiment, average] = data;
    const dateFormat = getDateFormatForGroup(dateRangeSet?.group);
    const metricsByDate = groupBy(sentiment, FieldName.Date);
    const averageByDate = keyBy(average, FieldName.Date);
    const dates = Object.keys(metricsByDate);
    const chart: Record<string, any> = {
        total: [],
        average: [],
        positive: [],
        neutral: [],
        negative: [],
        series: [],
        yAxis: [],
        categories: dates
            .map((date) => {
                const dateString = date.split('_')[0].trim();

                if (startsWith(dateRangeSet.period, 'fiscal')) {
                    const label = metricsByDate[date]?.[0]?.label;

                    if (label) {
                        return label;
                    }
                }

                const normalDate = DateTime.fromISO(dateString);
                const fiscalDate = DateTime.fromFormat(dateString, 'ccc LLL d, yyyy');

                if (normalDate.isValid) {
                    return normalDate.toLocaleString(dateFormat);
                }

                if (fiscalDate.isValid) {
                    return fiscalDate.toLocaleString(dateFormat);
                }
                return '';
            })
            .filter((item) => item !== ''),
    };

    const SENTIMENTS = [Sentiment.Positive, Sentiment.Neutral, Sentiment.Negative];

    each(dates, (date) => {
        const metrics = metricsByDate[date];
        const metricsBySentiment = keyBy(metrics, FieldName.Sentiment);
        const positiveSentiment = metricsBySentiment[Sentiment.Positive]?.metric || 0;
        const neutralSentiment = metricsBySentiment[Sentiment.Neutral]?.metric || 0;
        const negativeSentiment = metricsBySentiment[Sentiment.Negative]?.metric || 0;
        const averageRating = averageByDate[date]?.metric || 0;

        chart.average.push(roundRating(averageRating * 5));
        chart.positive.push({
            y: positiveSentiment,
            date,
            dateRangeGroup: dateRangeSet?.group,
            fiscalDateRange: metrics[0].fiscalDateRange,
        });
        chart.neutral.push({
            y: neutralSentiment,
            date,
            dateRangeGroup: dateRangeSet?.group,
            fiscalDateRange: metrics[0].fiscalDateRange,
        });
        chart.negative.push({
            y: negativeSentiment,
            date,
            dateRangeGroup: dateRangeSet?.group,
            fiscalDateRange: metrics[0].fiscalDateRange,
        });
        chart.total.push(positiveSentiment + neutralSentiment + negativeSentiment);
    });

    SENTIMENTS.forEach((sentiment) => {
        let name;
        let color;
        let data;

        switch (sentiment) {
            case Sentiment.Positive: {
                name = t('POSITIVE');
                color = SentimentColor.Positive;
                data = chart.positive;
                break;
            }
            case Sentiment.Neutral: {
                name = t('NEUTRAL');
                color = SentimentColor.Neutral;
                data = chart.neutral;
                break;
            }
            case Sentiment.Negative: {
                name = t('NEGATIVE');
                color = SentimentColor.Negative;
                data = chart.negative;
                break;
            }
            default: {
                break;
            }
        }

        chart.series.push({
            name,
            color,
            data,
            showInLegend: true,
            turboThreshold: 0,
            yAxis: 0,
            events: {
                click: (event: SeriesClickEventObject) => {
                    const point = event.point as HighchartPoint;

                    if (startsWith(dateRangeSet.period, 'fiscal')) {
                        return handleColumnClick?.(event, sentiment, undefined, point.fiscalDateRange);
                    }

                    const dateFilterValue = getDateFilterValue(point.date, getIntervalForGroup(point.dateRangeGroup));

                    return handleColumnClick?.(event, sentiment, undefined, dateFilterValue);
                },
                legendItemClick: (event: MouseEvent) => {
                    return handleLegendClick?.(event, sentiment);
                },
            },
        });
    });

    chart.series.push({
        name: t('AVERAGE_RATING'),
        showInLegend: true,
        color: Color.Turquoise3,
        data: chart.average,
        marker: {
            symbol: 'circle',
            fillColor: Color.Turquoise3,
            radius: 4,
            lineWidth: 2,
            lineColor: null,
        },
        type: 'line',
        yAxis: 1,
        lineColor: Color.Turquoise3,
        lineWidth: 1,
        turboThreshold: 0,
    });

    chart.yAxis.push(
        {
            title: {
                text: t('REVIEW_VOLUME'),
            },
            stackLabels: {
                enabled: true,
                align: 'center',
                style: {
                    color: Color.Grey2,
                    fontWeight: 'normal',
                    textOutline: null,
                },
            },
        },
        {
            title: {
                text: t('AVERAGE_RATING'),
                style: {
                    color: Color.Turquoise2,
                },
            },
            lineColor: Color.Grey5,
            labels: {
                style: {
                    color: Color.Turquoise2,
                },
            },
            min: 0,
            max: 5,
            opposite: true,
            tickInterval: 1,
            startOnTick: false,
            endOnTick: false,
            gridLineColor: 'transparent',
            minorGridLineWidth: 0,
        },
    );

    return chart;
};

const getSentimentBySourceData = (data: any[], args: GetChartArgs) => {
    const { t, sources, handleColumnClick, handleLegendClick } = args;
    let [sentiment, total] = data;
    const sourcesByUid = keyBy(sources, 'uid');
    const sourcesById = keyBy(sources, 'id');

    total = total.filter(
        (metric: any) =>
            sourcesByUid[get(metric, FieldName.SourceUid)] ||
            sourcesById[get(metric, FieldName.SourceUid).toUpperCase()],
    );
    sentiment = sentiment.filter(
        (metric: any) =>
            sourcesByUid[get(metric, FieldName.SourceUid)] ||
            sourcesById[get(metric, FieldName.SourceUid).toUpperCase()],
    );
    const metricsBySourceUid = groupBy(sentiment, FieldName.SourceUid);

    const chart: Record<string, any> = {
        total: [],
        positive: [],
        neutral: [],
        negative: [],
        series: [],
        yAxis: [],
        categories: total.map(
            (metric: any) =>
                sourcesByUid[get(metric, FieldName.SourceUid)]?.name ||
                sourcesById[get(metric, FieldName.SourceUid).toUpperCase()]?.name,
        ),
    };
    const SENTIMENTS = [Sentiment.Positive, Sentiment.Neutral, Sentiment.Negative];

    each(total, (metric) => {
        const metrics = metricsBySourceUid[get(metric, FieldName.SourceUid)];
        const metricsBySentiment = keyBy(metrics, FieldName.Sentiment);
        const positiveSentiment = metricsBySentiment[Sentiment.Positive]?.metric || 0;
        const neutralSentiment = metricsBySentiment[Sentiment.Neutral]?.metric || 0;
        const negativeSentiment = metricsBySentiment[Sentiment.Negative]?.metric || 0;

        chart.positive.push(positiveSentiment);
        chart.neutral.push(neutralSentiment);
        chart.negative.push(negativeSentiment);
        chart.total.push(positiveSentiment + neutralSentiment + negativeSentiment);
    });

    SENTIMENTS.forEach((sentiment) => {
        let name;
        let color;
        let data;

        switch (sentiment) {
            case Sentiment.Positive: {
                name = t('POSITIVE');
                color = SentimentColor.Positive;
                data = chart.positive;
                break;
            }
            case Sentiment.Neutral: {
                name = t('NEUTRAL');
                color = SentimentColor.Neutral;
                data = chart.neutral;
                break;
            }
            case Sentiment.Negative: {
                name = t('NEGATIVE');
                color = SentimentColor.Negative;
                data = chart.negative;
                break;
            }
            default: {
                break;
            }
        }

        chart.series.push({
            name,
            color,
            data,
            showInLegend: true,
            turboThreshold: 0,
            yAxis: 0,
            events: {
                click: (event: any) => {
                    const source = find(sources, { name: event.point?.category });

                    return handleColumnClick?.(event, sentiment, source?.id);
                },
                legendItemClick: (event: any) => {
                    const source = find(sources, { name: event.point?.category });

                    return handleLegendClick?.(event, sentiment, source?.id);
                },
            },
        });
    });

    chart.yAxis.push({
        title: {
            text: t('REVIEW_VOLUME'),
        },
        stackLabels: {
            enabled: true,
            align: 'center',
            style: {
                color: Color.Grey2,
                fontWeight: 'normal',
                textOutline: null,
            },
        },
    });

    return chart;
};

const getRespondedData = (data: any[], args: GetChartArgs) => {
    const { t, dateRangeSet, handleColumnClick, handleLegendClick, isYetiEnabled } = args;
    const [sentiment] = data;
    const dateFormat = getDateFormatForGroup(dateRangeSet?.group);
    const metricsByDate = groupBy(sentiment, FieldName.Date);
    const dates = Object.keys(metricsByDate);
    const chart: Record<string, any[]> = {
        total: [],
        yAxis: [],
        series: [],
        responded: [],
        unresponded: [],
        pending: [],
        notRespondable: [],
        categories: dates
            .map((date) => {
                const dateString = date.split('_')[0].trim();

                if (startsWith(dateRangeSet.period, 'fiscal')) {
                    const label = metricsByDate[date]?.[0]?.label;

                    if (label) {
                        return label;
                    }
                }

                const normalDate = DateTime.fromISO(dateString);
                const fiscalDate = DateTime.fromFormat(dateString, 'ccc LLL d, yyyy');

                if (normalDate.isValid) {
                    return normalDate.toLocaleString(dateFormat);
                }

                if (fiscalDate.isValid) {
                    return fiscalDate.toLocaleString(dateFormat);
                }
                return '';
            })
            .filter((item) => item !== ''),
    };

    each(dates, (date) => {
        const metrics = metricsByDate[date];
        const metricsByLabel = keyBy(metrics, FieldName.Labels);
        const responded = metricsByLabel[SystemLabel.Responded]?.metric || 0;
        const unresponded = metricsByLabel[SystemLabel.Unresponded]?.metric || 0;
        const notRespondable = metricsByLabel[SystemLabel.NotRespondable]?.metric || 0;

        if (!isYetiEnabled) {
            const pending = metricsByLabel[SystemLabel.PendingApproval]?.metric || 0;

            chart.pending!.push({
                y: pending,
                date,
                dateRangeGroup: dateRangeSet?.group,
            });
            chart.total!.push(responded + unresponded + pending + notRespondable);
        } else {
            chart.total!.push(responded + unresponded + notRespondable);
        }
        chart.responded!.push({
            y: responded,
            date,
            dateRangeGroup: dateRangeSet?.group,
            fiscalDateRange: metrics[0].fiscalDateRange,
        });
        chart.unresponded!.push({
            y: unresponded,
            date,
            dateRangeGroup: dateRangeSet?.group,
            fiscalDateRange: metrics[0].fiscalDateRange,
        });
        chart.notRespondable!.push({
            y: notRespondable,
            date,
            dateRangeGroup: dateRangeSet?.group,
            fiscalDateRange: metrics[0].fiscalDateRange,
        });
    });

    chart.series!.push(
        {
            name: t('RESPONDED'),
            color: Color.Turquoise1,
            data: chart.responded,
            turboThreshold: 0,
            yAxis: 0,
            events: {
                click: (event: SeriesClickEventObject) => {
                    const point = event.point as HighchartPoint;

                    if (startsWith(dateRangeSet.period, 'fiscal')) {
                        return handleColumnClick?.(event, SystemLabel.Responded, undefined, point.fiscalDateRange);
                    }

                    const dateFilterValue = getDateFilterValue(point.date, getIntervalForGroup(point.dateRangeGroup));

                    return handleColumnClick?.(event, SystemLabel.Responded, undefined, dateFilterValue);
                },
                legendItemClick: (event: MouseEvent) => {
                    return handleLegendClick?.(event, SystemLabel.Responded);
                },
            },
        },
        {
            name: t('UNRESPONDED'),
            color: Color.Grey4,
            data: chart.unresponded,
            turboThreshold: 0,
            yAxis: 0,
            events: {
                click: (event: SeriesClickEventObject) => {
                    const point = event.point as HighchartPoint;

                    if (startsWith(dateRangeSet.period, 'fiscal')) {
                        return handleColumnClick?.(event, SystemLabel.Unresponded, undefined, point.fiscalDateRange);
                    }
                    const dateFilterValue = getDateFilterValue(point.date, getIntervalForGroup(point.dateRangeGroup));

                    return handleColumnClick?.(event, SystemLabel.Unresponded, undefined, dateFilterValue);
                },
                legendItemClick: (event: MouseEvent) => {
                    return handleLegendClick?.(event, SystemLabel.Unresponded);
                },
            },
        },
        {
            name: t('NOT_RESPONDABLE'),
            color: Color.Green1,
            data: chart.notRespondable,
            turboThreshold: 0,
            yAxis: 0,
            events: {
                click: (event: SeriesClickEventObject) => {
                    const point = event.point as HighchartPoint;

                    if (startsWith(dateRangeSet.period, 'fiscal')) {
                        return handleColumnClick?.(event, SystemLabel.Unresponded, undefined, point.fiscalDateRange);
                    }
                    const dateFilterValue = getDateFilterValue(point.date, getIntervalForGroup(point.dateRangeGroup));

                    return handleColumnClick?.(event, SystemLabel.NotRespondable, undefined, dateFilterValue);
                },
                legendItemClick: (event: MouseEvent) => {
                    return handleLegendClick?.(event, SystemLabel.NotRespondable);
                },
            },
        },
    );

    if (!isYetiEnabled) {
        chart.series!.push({
            name: t('PENDING_APPROVAL'),
            color: Color.DarkBlue1,
            data: chart.pending,
            turboThreshold: 0,
            yAxis: 0,
            events: {
                click: (event: SeriesClickEventObject) => {
                    const point = event.point as any;
                    const dateFilterValue = getDateFilterValue(point.date, getIntervalForGroup(point.dateRangeGroup));

                    return handleColumnClick?.(event, SystemLabel.PendingApproval, undefined, dateFilterValue);
                },
                legendItemClick: (event: MouseEvent) => {
                    return handleLegendClick?.(event, SystemLabel.PendingApproval);
                },
            },
        });
    }

    chart.yAxis.push({
        title: {
            text: t('REVIEW_VOLUME'),
        },
        stackLabels: {
            enabled: true,
            align: 'center',
            style: {
                color: Color.Grey2,
                fontWeight: 'normal',
                textOutline: null,
            },
        },
    });

    return chart;
};

export const convertToSentimentBreakdownMetrics = (data: any[]) => {
    const metricsBySentiment = keyBy(data[0], FieldName.Sentiment);
    const positive = metricsBySentiment[Sentiment.Positive]?.metric || 0;
    const neutral = metricsBySentiment[Sentiment.Neutral]?.metric || 0;
    const negative = metricsBySentiment[Sentiment.Negative]?.metric || 0;
    const total = positive + neutral + negative;

    return { positive, neutral, negative, total };
};

export const convertToOverviewMetrics = (currentPeriodData: any[], previousPeriodData: any[], args: any) => {
    const { t, dateRangeSet } = args;
    const current = flatten(currentPeriodData);
    const currentByKey = keyBy(current, FieldName.Labels);
    const previous = flatten(previousPeriodData);
    const previousByKey = keyBy(previous, FieldName.Labels);
    const metricIds = map(OverviewMetrics, 'id');

    return metricIds.map((metricId) => {
        const metadata = MetricMetadata[metricId as MetricId];
        const currentPeriodMetric = currentByKey[metadata?.key!];
        const previousPeriodMetric = previousByKey[metadata?.key!];
        const displayMetric: { [key: string]: any } = {
            ...metadata,
            showDeltaAsAbs: true,
            noPreviousValueLabel: t('METRICS_NO_DATA'),
        };

        displayMetric.label = t(metadata?.label);
        displayMetric.tooltip = t(metadata?.tooltip);
        if (currentPeriodMetric?.percentage) {
            displayMetric.percentage = currentPeriodMetric?.percentage;
            displayMetric.percentTooltip = t(metadata?.percentTooltip);
        }
        if (currentPeriodMetric) {
            displayMetric.value = currentPeriodMetric?.count;
        } else if (
            metricId === MetricId.Responded ||
            metricId === MetricId.Unresponded ||
            metricId === MetricId.NotRespondable
        ) {
            displayMetric.percentage = `0%`;
            displayMetric.percentTooltip = t(metadata?.percentTooltip);
        }
        displayMetric.previousLabel = dateRangeSet.previous.labelShort;
        displayMetric.previousValue = previousPeriodMetric?.count;
        displayMetric.delta = (displayMetric.value ?? 0) - displayMetric.previousValue;

        return displayMetric;
    });
};

const getAverageRatingReqs = (filters?: Filter[]): BatchAggregateRequest => {
    return {
        aggregateRequests: [
            {
                filters: [
                    ...filters!,
                    {
                        name: FieldName.Type,
                        values: [String(CommentType.Review)],
                    },
                ],
                groupBys: [
                    {
                        name: FieldName.TenantId,
                        label: FieldName.TenantId,
                    },
                ],
                metrics: [
                    {
                        label: 'metric',
                        metricType: MetricType.Average,
                        name: `${FieldName.Properties}.${CommentProperty.Rating}.rating.normalizedRatingValue`,
                    },
                ],
            },
        ],
    };
};

const getIntervalForGroup = (group: number): number => {
    switch (group) {
        case Group.Day:
        case Group.Week:
        case Group.DayOfWeek:
        case Group.TimeOfDay:
        case Group.FiscalWeek: {
            return Interval.Day;
        }
        case Group.Month:
        case Group.Quarter:
        case Group.Year:
        case Group.FiscalMonth:
        case Group.FiscalQuarter:
        case Group.FiscalYear: {
            return Interval.Month;
        }
        default: {
            return Interval.None;
        }
    }
};

const getSentimentTrendReqs = (filters?: Filter[], dateRangeSet?: any): BatchAggregateRequest => {
    let interval = getIntervalForGroup(dateRangeSet?.group);

    if (dateRangeSet?.period === 'all_time') {
        interval = Interval.Year;
    }

    return {
        aggregateRequests: [
            {
                filters: [
                    ...filters!,
                    {
                        name: FieldName.Type,
                        values: [String(CommentType.Review)],
                    },
                ],
                groupBys: [
                    {
                        name: FieldName.Date,
                        label: FieldName.Date,
                        interval,
                        sorts: [
                            {
                                name: FieldName.Date,
                                sortOrder: SortOrder.Asc,
                            },
                        ],
                    },
                    {
                        name: FieldName.Sentiment,
                        label: FieldName.Sentiment,
                    },
                ],
                metrics: [
                    {
                        label: 'metric',
                        name: FieldName.Id,
                        metricType: MetricType.Count,
                    },
                ],
                sorts: [],
                limit: LIMIT,
            },
            {
                filters: [
                    ...filters!,
                    {
                        name: FieldName.Type,
                        values: [String(CommentType.Review)],
                    },
                ],
                groupBys: [
                    {
                        name: FieldName.Date,
                        label: FieldName.Date,
                        interval,
                        sorts: [
                            {
                                name: FieldName.Date,
                                sortOrder: SortOrder.Asc,
                            },
                        ],
                    },
                ],
                metrics: [
                    {
                        label: 'metric',
                        metricType: MetricType.Average,
                        name: FieldName.NormalizedRating,
                    },
                ],
                sorts: [],
                limit: LIMIT,
            },
        ],
    };
};

const getSentimentBySourceReqs = (filters?: Filter[]): BatchAggregateRequest => {
    return {
        aggregateRequests: [
            {
                filters: [
                    ...filters!,
                    {
                        name: FieldName.Type,
                        values: [String(CommentType.Review)],
                    },
                ],
                groupBys: [
                    {
                        name: FieldName.SourceUid,
                        label: FieldName.SourceUid,
                    },
                    {
                        name: FieldName.Sentiment,
                        label: FieldName.Sentiment,
                    },
                ],
                metrics: [
                    {
                        label: 'metric',
                        name: FieldName.Id,
                        metricType: MetricType.Count,
                    },
                ],
                sorts: [],
                limit: LIMIT,
            },
            {
                filters: [
                    ...filters!,
                    {
                        name: FieldName.Type,
                        values: [String(CommentType.Review)],
                    },
                ],
                groupBys: [
                    {
                        name: FieldName.SourceUid,
                        label: FieldName.SourceUid,
                    },
                ],
                metrics: [
                    {
                        label: 'metric',
                        name: FieldName.Id,
                        metricType: MetricType.Count,
                    },
                ],
                sorts: [],
                limit: LIMIT,
            },
        ],
    };
};

const getRespondedReqs = (filters?: Filter[], dateRangeSet?: any): BatchAggregateRequest => {
    let interval = getIntervalForGroup(dateRangeSet?.group);

    if (dateRangeSet?.period === 'all_time') {
        interval = Interval.Year;
    }

    return {
        aggregateRequests: [
            {
                filters: [
                    ...filters!,
                    {
                        name: FieldName.Type,
                        values: [String(CommentType.Review)],
                    },
                    {
                        name: FieldName.Labels,
                        values: [
                            String(SystemLabel.Responded),
                            String(SystemLabel.Unresponded),
                            String(SystemLabel.NotRespondable),
                        ],
                    },
                ],
                groupBys: [
                    {
                        name: FieldName.Date,
                        label: FieldName.Date,
                        interval,
                        sorts: [
                            {
                                name: FieldName.Date,
                                sortOrder: SortOrder.Asc,
                            },
                        ],
                    },
                    {
                        name: FieldName.Labels,
                        label: FieldName.Labels,
                    },
                ],
                metrics: [
                    {
                        label: 'metric',
                        name: FieldName.Id,
                        metricType: MetricType.Count,
                    },
                ],
                sorts: [],
                limit: LIMIT,
            },
        ],
    };
};

const getTopCategoriesReqs = (filters?: Filter[]): BatchAggregateRequest => {
    return {
        aggregateRequests: [
            {
                filters: [
                    ...filters!,
                    {
                        name: FieldName.Type,
                        values: [String(CommentType.Review)],
                    },
                ],
                groupBys: [
                    {
                        name: FieldName.CategoryId,
                        label: FieldName.CategoryId,
                    },
                ],
                sorts: [
                    {
                        name: 'metric',
                        sortOrder: SortOrder.Desc,
                    },
                ],
                metrics: [
                    {
                        metricType: MetricType.Count,
                        name: FieldName.Id,
                        label: 'metric',
                    },
                ],
                limit: 5,
            },
        ],
    };
};

export const getSentimentBreakdownReqs = (filters?: Filter[]): BatchAggregateRequest => {
    return {
        aggregateRequests: [
            {
                filters: [
                    ...filters!,
                    {
                        name: FieldName.Type,
                        values: [String(CommentType.Review)],
                    },
                ],
                groupBys: [
                    {
                        name: FieldName.Sentiment,
                        label: FieldName.Sentiment,
                    },
                ],
                sorts: [],
                metrics: [
                    {
                        metricType: MetricType.Count,
                        name: FieldName.Sentiment,
                        label: 'metric',
                    },
                ],
            },
        ],
    };
};

export const getReviewsOverviewReqs = (filters?: Filter[]): BatchAggregateRequest => {
    return {
        aggregateRequests: [
            {
                filters: [
                    ...filters!,
                    {
                        name: FieldName.Type,
                        values: [String(CommentType.Review)],
                    },
                ],
                groupBys: [
                    {
                        name: FieldName.TenantId,
                        label: FieldName.TenantId,
                    },
                ],
                sorts: [],
                metrics: [
                    {
                        metricType: MetricType.Count,
                        name: FieldName.Id,
                        label: 'count',
                    },
                ],
            },
            {
                filters: [
                    ...filters!,
                    {
                        name: FieldName.Type,
                        values: [String(CommentType.Review)],
                    },
                ],
                groupBys: [
                    {
                        name: FieldName.Labels,
                        label: FieldName.Labels,
                    },
                ],
                sorts: [],
                metrics: [
                    {
                        metricType: MetricType.Count,
                        name: FieldName.Id,
                        label: 'count',
                    },
                ],
            },
        ],
    };
};

export const addMetricFilter = (selectedFilters: Filter[], filterName: string, filterValue: any): Filter[] => {
    const currentFilters = clone(selectedFilters);
    const newSelectedFilters = remove(currentFilters!, { name: filterName });
    const metricFilter: Filter = {
        name: filterName,
        values: [String(filterValue)],
    };

    newSelectedFilters.push(metricFilter);

    return newSelectedFilters;
};

export const getLocationsCountReqs = (filters?: Filter[]): BatchAggregateRequest => {
    return {
        aggregateRequests: [
            {
                filters: [
                    ...filters!,
                    {
                        name: FieldName.Type,
                        values: [String(CommentType.Review)],
                    },
                ],
                groupBys: [
                    {
                        name: FieldName.TenantId,
                        label: FieldName.TenantId,
                    },
                ],
                metrics: [
                    {
                        label: 'count',
                        metricType: MetricType.UniqueCount,
                        name: FieldName.EntityId,
                    },
                ],
            },
        ],
    };
};
